import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import ChatWidget from './ChatWidget';
import SiteNav from './SiteNav';
import SiteFooter from './SiteFooter';

const T = {
  fa: {
    dir: 'rtl',
    supportersTitle: 'تازه‌ترین حامیان',
    anonymousSupporter: 'حامی ناشناس',
    noSupporters: 'هنوز حمایتی ثبت نشده؛ اولین حامی این ماجراجویی باش!',
    loading: 'در حال بارگذاری...'
  },
  en: {
    dir: 'ltr',
    supportersTitle: 'Recent Supporters',
    anonymousSupporter: 'Anonymous supporter',
    noSupporters: 'No support has been recorded yet. Be the first supporter of this adventure!',
    loading: 'Loading...'
  }
};

function formatSupportAmount(amount, currency, lang = 'fa') {
  if (!amount) return '';
  if (lang !== 'fa') {
    return `${Number(amount).toLocaleString('en-US')} ${currency || 'USD'}`;
  }
  const label = currency === 'IRR' ? 'ریال' : 'تومان';
  return `${Number(amount).toLocaleString('fa-IR')} ${label}`;
}

export default function SupportPage() {
  const { i18n } = useTranslation();
  const lang = i18n.language || 'fa';
  const t = T[lang] || T.en;

  const [content, setContent] = useState(null);
  const [supportInfo, setSupportInfo] = useState({ supportUrl: 'https://reymit.ir/m4tinbeigi', supporters: [] });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    Promise.all([
      axios.get(`/api/landing?_=${Date.now()}`).catch(() => null),
      axios.get('/api/support').catch(() => null)
    ]).then(([landingRes, supportRes]) => {
      if (!mounted) return;
      const fetched = landingRes?.data?.content?.[lang];
      if (fetched) setContent(fetched);
      if (supportRes?.data?.success) {
        setSupportInfo({
          supportUrl: supportRes.data.supportUrl || 'https://reymit.ir/m4tinbeigi',
          supporters: supportRes.data.supporters || []
        });
      }
      setLoading(false);
    });
    return () => { mounted = false; };
  }, [lang]);

  return (
    <div
      dir={t.dir}
      style={{
        minHeight: '100vh',
        background: 'radial-gradient(circle at 50% 0%, #241a3a 0%, #140f22 55%, #0c0916 100%)'
      }}
    >
      <SiteNav />
      <div style={{ maxWidth: 760, margin: '0 auto', padding: '120px 20px 80px' }}>
        {loading && !content ? (
          <p style={{ color: '#a396c2', textAlign: 'center' }}>{t.loading}</p>
        ) : (
          <div
            style={{
              background: 'rgba(255,255,255,0.04)',
              border: '1px solid rgba(212,160,23,0.3)',
              borderRadius: 16,
              padding: '32px 28px',
              textAlign: 'center'
            }}
          >
            <span style={{ fontSize: '2.4rem' }}>💛</span>
            <h1 style={{ color: '#f3e9d2', fontSize: '1.9rem', margin: '12px 0' }}>
              {content?.supportTitle}
            </h1>
            <p style={{ color: '#c9bfa8', lineHeight: 1.9, marginBottom: 24 }}>
              {content?.supportDesc}
            </p>
            <a
              href={supportInfo.supportUrl}
              target="_blank"
              rel="noreferrer"
              style={{
                display: 'inline-block',
                background: 'linear-gradient(135deg,#d4af37,#b8860b)',
                color: '#1a1230',
                fontWeight: 700,
                padding: '12px 32px',
                borderRadius: 10,
                textDecoration: 'none'
              }}
            >
              {content?.supportButton}
            </a>

            <div style={{ marginTop: 36, paddingTop: 24, borderTop: '1px solid rgba(255,255,255,0.08)' }}>
              <h3 style={{ color: '#f3e9d2', marginBottom: 14 }}>{t.supportersTitle}</h3>
              {supportInfo.supporters.length === 0 ? (
                <p style={{ color: '#a396c2' }}>{t.noSupporters}</p>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {supportInfo.supporters.map((supporter) => (
                    <div
                      key={supporter.id}
                      style={{
                        background: 'rgba(255,255,255,0.03)',
                        border: '1px solid rgba(255,255,255,0.08)',
                        borderRadius: 8,
                        padding: '10px 14px',
                        textAlign: lang === 'fa' ? 'right' : 'left'
                      }}
                    >
                      <strong style={{ color: '#f3e9d2' }}>{supporter.name || t.anonymousSupporter}</strong>
                      {supporter.amount && (
                        <span style={{ color: '#d4af37', marginInlineStart: 10 }}>
                          {formatSupportAmount(supporter.amount, supporter.currency, lang)}
                        </span>
                      )}
                      {supporter.message && (
                        <div style={{ color: '#a396c2', fontSize: '0.9rem', marginTop: 4 }}>
                          {supporter.message}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
      <SiteFooter />
      <ChatWidget />
    </div>
  );
}
